<template>
  <div id="map" class="map-container" />
</template>

<script>
import PopupClass from '@/pure/PopupClass';
import { createApp, reactive } from 'vue';
import { mapGetters } from 'vuex';
import MarkerClusterer from '@/pure/MarkerClusterer';
import { Linear, TweenLite } from 'gsap';
import assetProvider from '@/pure/assetProvider';
import Theme from './theme';
import PopupFutureSite from './Popup/FutureSite';
import PopupActualSite from './Popup/ActualSite';
import PopupCollaborator from './Popup/Collaborator';
import PopupPOI from './Popup/POI';
import store from '@/store';
import i18n from '@/i18n';
import { useSignalR } from '@dreamonkey/vue-signalr';
import config from '@/config';

let Popup;
const MaxZoom = 15;

export default {
  computed: {
    ...mapGetters([
      'sites',
      'poiVisibilityKey',
      'poiVisibility',
      'isDesktop',
      'study',
      'activeIndex',
      'filters',
      'globalFilters',
      'ruleFilters',
      'isochrone',
      'isochroneDecay',
      'isochroneDisplay',
      'publicTransportLinesShown',
      'airQualityIndexOverlayShown',
    ]),

    ...mapGetters({ collaborators: 'collaboratorsNotFilteredByTime' }),

    globalCollaboratorFilter() {
      return this.globalFilters.collaborators;
    },

    globalTargetSiteFilter() {
      return this.globalFilters.targetSites;
    },

    globalCurrentMatchingTime() {
      return this.globalFilters.currentMatchingTime;
    },
  },

  watch: {
    'study.maximum_travel_time_enabled'() {
      this.updateExcludedCollaborators();
    },

    'study.public_maximum_travel_time'() {
      this.updateExcludedCollaborators();
    },

    'study.private_maximum_travel_time'() {
      this.updateExcludedCollaborators();
    },

    'study.bicycle_maximum_travel_time'() {
      this.updateExcludedCollaborators();
    },

    'study.walking_maximum_travel_time'() {
      this.updateExcludedCollaborators();
    },

    globalCurrentMatchingTime() {
      this.updateExcludedCollaborators();
    },

    study: {
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal.sites === oldVal.sites) return;

        this.targetSiteMarkers.forEach(siteMarker => siteMarker.setMap(null));

        this.targetSiteMarkers.splice(0, this.targetSiteMarkers.length);

        this.study?.sites
          .filter(item => item.type === 'Target')
          .map(site => this.addTargetSiteMarker(site, site.order))
          .forEach(siteMarker => this.targetSiteMarkers.push(siteMarker));
      },

      deep: true,
    },

    poiVisibilityKey(newFilter, oldFilter) {
      const addedPoi = newFilter.filter(x => !oldFilter.includes(x));
      const removedPoi = oldFilter.filter(x => !newFilter.includes(x));

      const currentZoom = this.map.getZoom();
      if (currentZoom <= MaxZoom) return;

      this.poiMarkers.forEach(m => {
        if (addedPoi.includes(m.type)) return this.fadeMarker(m, 1);
        if (removedPoi.includes(m.type)) return this.fadeMarker(m, 0);
      });
    },

    isochrone(data) {
      if (!data) {
        this.heatmap.setMap(null);
        return;
      }

      if (this.heatmap) {
        this.heatmap.setMap(null);
      }

      const positions = data.reduce((acc, cur) => {
        cur.j.forEach(item => {
          const p = new google.maps.LatLng(item.c.lat, item.c.lng);
          const d = item.d;
          acc.push({
            weight: d,
            location: p,
          });
        });
        return acc;
      }, []);
      const gradient = ['rgba(52, 204, 131, 0)', 'rgba(251, 210, 30, 1)', 'rgba(255, 155, 52, 1)', 'rgba(188, 20, 26, 1)'];
      const radius = this.getRadius();
      this.heatmap = new this.GM.visualization.HeatmapLayer({
        data: positions,
        dissipating: true,
        gradient,
        radius: radius,
      });
      this.heatmap.setMap(this.isochroneDisplay ? this.map : null);

      this._listeners.push(this.GM.event.addListener(this.map, 'zoom_changed', () => this.isochroneZoomHandler()));
    },

    isochroneDecay(newVal) {
      if (this.heatmap) {
        const radius = (1 / this.getMetersPerPx()) * 750 * newVal;
        this.heatmap.set('radius', radius);
      }
    },

    isochroneDisplay(newVal) {
      if (this.heatmap) {
        this.heatmap.setMap(newVal ? this.map : null);
      }
    },

    publicTransportLinesShown(newVal) {
      if (!this.transitLayer) return;

      this.transitLayer.setMap(newVal ? this.map : null);
      this.updateRailStationVisibility(newVal);
    },

    airQualityIndexOverlayShown() {
      this.updateAirQualityOverlay();
    },

    activeIndex(newVal, oldVal) {
      if (oldVal >= 0) {
        if (this.targetSiteMarkers[oldVal]) {
          this.targetSiteMarkers[oldVal].customUnselect();
        }
      }
      if (newVal >= 0) {
        if (this.targetSiteMarkers[newVal]) {
          this.targetSiteMarkers[newVal].customSelect();
        }
      }
    },

    globalFilters: {
      handler() {
        if (!this.study) return;

        this.study.sites
          .filter(item => item.type === 'Target')
          .forEach((site, idx) => {
            if (!site.lat_lng || !site.lat_lng[0] || !site.lat_lng[1]) return;

            this.targetSiteMarkers[idx].popup.instanceProps.datum = this.getTargetSiteDatum(site, idx);
          });

        this.study.sites
          .filter(item => item.type === 'Actual')
          .forEach((site, idx) => {
            this.actualSiteMarkers.getAt(idx).popup.instanceProps.datum = this.getActualSiteDatum(site, idx);
          });
      },

      deep: true,
    },

    collaborators(newVal) {
      if (!this.study) return;

      this.filterMarkers();

      this.study.sites
        .filter(item => item.type === 'Target')
        .forEach((site, idx) => {
          if (!site.lat_lng || !site.lat_lng[0] || !site.lat_lng[1]) return;

          if (!this.targetSiteMarkers[idx]) return;

          this.targetSiteMarkers[idx].popup.instanceProps.datum = this.getTargetSiteDatum(site, idx);
        });

      this.study.sites
        .filter(item => item.type === 'Actual')
        .forEach((site, idx) => {
          if (!this.actualSiteMarkers.getAt(idx)) return;

          this.actualSiteMarkers.getAt(idx).popup.instanceProps.datum = this.getActualSiteDatum(site, idx);
        });

      const markers = this.markerCluster.getMarkers();

      const markersToRemove = markers.reduce((acc, marker) => {
        if (!newVal.find(item => marker.id === item.id)) {
          acc.push(marker);
        }
        return acc;
      }, []);

      const markersToAdd = newVal.reduce((acc, person) => {
        if (!markers.find(marker => person.id === marker.id)) {
          acc.push(this.collaboratorsMarkers.find(marker => marker.id === person.id));
        }
        return acc;
      }, []);

      if (markersToRemove.length > 0) {
        this.markerCluster.removeMarkers(markersToRemove);
      }

      if (markersToAdd.length > 0) {
        this.markerCluster.addMarkers(markersToAdd);
      }
    },

    globalCollaboratorFilter(newVal) {
      this.updateVisibilityMarkers(newVal);
    },

    globalTargetSiteFilter: {
      handler(newVal) {
        this.updateVisibilityTargetSiteMarkers(newVal);
      },

      deep: true,
    },
  },

  created() {
    this.$socket = useSignalR();

    this.GM = google.maps;
    this.popups = [];

    this.targetSiteMarkers = [];
    this.actualSiteMarkers = new this.GM.MVCArray();
    this.collaboratorsMarkers = [];
    this.poiMarkers = [];
    this.bounds = new this.GM.LatLngBounds();
  },

  mounted() {
    Popup = PopupClass(this.GM);

    this.sites
      .filter(site => site.type === 'Target')
      .sort((a, b) => a.index - b.index)
      .forEach((site, index) => (site.order = index));

    this._listeners = [];

    this.circleIcon = {
      path: this.GM.SymbolPath.CIRCLE,
      scale: 10,
      fillOpacity: 1.0,
      fillColor: '#f7a61f',
      strokeOpacity: 0.0,
    };

    this.circleIconExcluded = {
      url: assetProvider.greyCrossCircle,
      size: new this.GM.Size(20, 20),
      origin: new this.GM.Point(0, 0),
      anchor: new this.GM.Point(10, 10),
      scaledSize: new this.GM.Size(20, 20),
    };

    this.map = new this.GM.Map(this.$el, {
      zoom: 13,
      center: { lat: 48.864716, lng: 2.349014 },
      disableDefaultUI: true,
      zoomControl: true,
      scaleControl: true,
      styles: Theme.mapStyle,
      mapTypeControlOptions: {
        mapTypeIds: ['roadmap', 'satellite'],
        style: this.GM.MapTypeControlStyle.DROPDOWN_MENU,
        position: this.GM.ControlPosition.TOP_RIGHT,
      },
      mapTypeControl: true,
      mapTypeId: 'roadmap',
    });

    this.transitLayer = new this.GM.TransitLayer();

    this.airQualityIndexOverlay = new this.GM.ImageMapType({
      getTileUrl: (coord, zoom) => `${config.API_URL}/map-tiles/${zoom}/${coord.x}/${coord.y}`,
      name: 'AirQuality',
    });

    this.buildCollaboratorsMarkers();

    this.updateExcludedCollaborators();

    this.updatePublicTransportLines();

    this.updateAirQualityOverlay();

    if (this.study && this.study.sites) {
      this.study.sites
        .filter(item => item.type === 'Target')
        .forEach((site, idx) => {
          if (!site.lat_lng || !site.lat_lng[0] || !site.lat_lng[1]) return;

          const types = ['supermarket', 'restaurant', 'gym', 'school'];
          for (const type in types) {
            const request = {
              location: new this.GM.LatLng(site.lat_lng[0], site.lat_lng[1]),
              radius: this.study.poi_radius || 500,
              type: types[type],
              name: types[type] === 'school' ? 'crèche' : null,
            };

            setTimeout(
              () => {
                const service = new this.GM.places.PlacesService(this.map);
                service.nearbySearch(request, (results, status) => {
                  if (status !== this.GM.places.PlacesServiceStatus.OK) return;

                  for (let i = 0; i < results.length; i++) {
                    const place = results[i];
                    const poiMarker = this.addPOIMarker(place, site.id, types[type]);
                    this.poiMarkers.push(poiMarker);
                  }
                });
              },
              type * idx * 500,
            );
          }

          const siteMarker = this.addTargetSiteMarker(site, site.order);
          this.targetSiteMarkers.push(siteMarker);
        });

      this.study?.sites
        .filter(item => item.type === 'Actual')
        .forEach((site, idx) => {
          this.actualSiteMarkers.set(site.name, this.map);

          if (!site.lat_lng || !site.lat_lng[0] || !site.lat_lng[1]) return;

          const currentMarker = this.addActualSiteMarker(site, idx);
          currentMarker.bindTo('map', this.actualSiteMarkers, site.name);
          this.actualSiteMarkers.push(currentMarker);
        });
    }

    this._listeners.push(this.GM.event.addListener(this.map, 'zoom_changed', () => this.zoomHandler()));
    this.map.fitBounds(this.bounds);
  },

  beforeUnmount() {
    this.$store.dispatch('clearIsochrone');
    if (this.transitLayer) {
      this.transitLayer.setMap(null);
    }
    this.clearListeners();
    this.markerCluster.clearMarkers();
    this.popups.forEach(popup => {
      popup.instance.unmount();
      popup.destroy();
    });
  },

  methods: {
    getMetersPerPx(getMetersPerPx) {
      const lat = this.map.getCenter().lat();
      const zoom = getMetersPerPx || this.map.getZoom();
      return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
    },

    getRadius(currentZoom) {
      return (1 / this.getMetersPerPx(currentZoom)) * 750 * this.isochroneDecay;
    },

    isochroneZoomHandler() {
      const currentZoom = this.map.getZoom();
      if (this.heatmap) {
        const radius = this.getRadius(currentZoom);
        this.heatmap.set('radius', radius);
      }
    },

    clearListeners() {
      this._listeners.forEach(e => {
        this.GM.event.removeListener(e);
        e.listener = null;
      });
    },

    updatePublicTransportLines() {
      if (!this.transitLayer) return;

      const shouldShow = this.publicTransportLinesShown;
      this.transitLayer.setMap(shouldShow ? this.map : null);
      this.updateRailStationVisibility(shouldShow);
    },

    updateRailStationVisibility(shown) {
      if (!this.map) return;

      const styles = shown
        ? Theme.mapStyle
        : Theme.mapStyle.map(rule => (rule.featureType === 'transit.station.rail' ? { ...rule, stylers: [{ visibility: 'off' }] } : rule));
      this.map.setOptions({ styles });
    },

    updateAirQualityOverlay() {
      const shouldShow = this.airQualityIndexOverlayShown && this.map.getZoom() >= 10;
      this.map.overlayMapTypes.setAt(0, shouldShow ? this.airQualityIndexOverlay : null);
    },

    zoomHandler() {
      this.updateAirQualityOverlay();

      const currentZoom = this.map.getZoom();

      const poiVisibility = Object.keys(this.poiVisibility).filter(x => this.poiVisibility[x] === true);

      if (currentZoom > MaxZoom) {
        this.poiMarkers.forEach(m => {
          if (!poiVisibility.includes(m.type)) return;
          this.fadeMarker(m, 1);
        });
      } else {
        this.poiMarkers.forEach(m => {
          this.fadeMarker(m, 0);
          m.popup.close();
        });
      }
    },

    buildCollaboratorsMarkers() {
      this.collaborators
        .filter(collaborator => collaborator.latLng.length === 2)
        .map((collaborator, idx) => this.addCollaboratorMarker(collaborator, idx))
        .forEach(collaboratorMarker => this.collaboratorsMarkers.push(collaboratorMarker));

      this.markerCluster = new MarkerClusterer(this.map, this.collaboratorsMarkers, {
        ignoreHidden: true,
        styles: [
          {
            url: assetProvider.m1,
            width: 54,
            height: 54,
            fontFamily: 'Source Sans Pro',
            textSize: 10,
            textColor: '#ffffff',
          },
          {
            url: assetProvider.m2,
            width: 56,
            height: 56,
            fontFamily: 'Source Sans Pro',
            textSize: 10,
            textColor: '#ffffff',
          },
        ],
      });
    },

    filterMarkers() {
      this.ruleFilters.actualSite.forEach(id => {
        if (this.filters.actualSite.length === 0 || this.filters.actualSite.find(item => item.toString() === id.toString())) {
          if (!this.actualSiteMarkers.get(id)) {
            this.actualSiteMarkers.set(id, this.map);
          }
        } else {
          if (this.actualSiteMarkers.get(id)) {
            this.actualSiteMarkers.set(id, null);
          }
        }
      });
    },

    fadeMarker(marker, to) {
      const x = {
        opacity: marker.opacity,
      };
      TweenLite.killTweensOf(marker);
      TweenLite.to(x, 0.3, {
        opacity: to,
        ease: Linear.easeNone,
        onUpdate: () => {
          marker.setOpacity(x.opacity);
        },
      });

      marker.clickable = !(to <= 0);
    },

    updateVisibilityTargetSiteMarkers(filters) {
      for (let i = 0; i < this.targetSiteMarkers.length; i++) {
        const marker = this.targetSiteMarkers[i];
        const visibility = !!filters.find(site => site === marker.id);
        this.fadeMarker(marker, visibility ? 1 : 0);
      }
    },

    updateVisibilityMarkers(visibility) {
      for (let i = 0; i < this.collaboratorsMarkers.length; i++) {
        this.collaboratorsMarkers[i].setVisible(visibility);
      }

      this.markerCluster.repaint();
    },

    generatePopup(latLng, popupContent) {
      return new Popup(latLng, popupContent, this.map);
    },

    createPopup(datum, map, gMarker, template) {
      const props = reactive({ datum });
      const instance = createApp(template, props).use(i18n).use(store);
      const app = instance.mount(document.createElement('div'));

      const popup = this.generatePopup(gMarker.position, app.$el);
      popup.instance = instance;
      popup.instanceProps = props;
      this.popups.push(popup);

      popup.open = function () {
        this.anchor.classList.remove('leaving');
        if (this.timeout) {
          clearTimeout(this.timeout);
        }
        this.setMap(map);
        props.opened = true;
      };

      popup.close = function () {
        if (this.timeout) {
          clearTimeout(this.timeout);
        }
        this.anchor.classList.add('leaving');
        this.timeout = setTimeout(() => {
          this.setMap(null);
          props.opened = false;
        }, 250);
      };

      app.$.emit = event => {
        if (event !== 'close') return;
        popup.close();
      };

      return popup;
    },

    generateIcon(image) {
      return {
        url: image,
        size: new this.GM.Size(37, 52),
        origin: new this.GM.Point(0, 0),
        anchor: new this.GM.Point(18, 49),
        scaledSize: new this.GM.Size(37, 52),
      };
    },

    generatePlaceIcon(types) {
      let image = '';

      if (types.includes('supermarket')) {
        image = assetProvider.poiSupermarket;
      } else if (types.includes('gym')) {
        image = assetProvider.poiSport;
      } else if (types.includes('restaurant')) {
        image = assetProvider.poiRestaurant;
      } else if (types.includes('school')) {
        image = assetProvider.poiNursery;
      }

      return {
        url: image,
        size: new this.GM.Size(20, 20),
        origin: new this.GM.Point(0, 0),
        anchor: new this.GM.Point(10, 10),
        scaledSize: new this.GM.Size(20, 20),
      };
    },

    addPOIMarker(poi, siteId, type) {
      const id = siteId + ' ' + poi.id;
      const position = poi.geometry.location;
      this.bounds.extend(position);

      const gMarker = new this.GM.Marker({
        map: this.map,
        position,
        id,
        icon: this.generatePlaceIcon(poi.types),
        zIndex: 0,
        type,
      });

      if (this.map.getZoom() > MaxZoom) {
        gMarker.setOpacity(1);
        gMarker.clickable = true;
      } else {
        gMarker.setOpacity(0);
        gMarker.clickable = false;
      }

      const datum = { name: poi.name };

      const popup = this.createPopup(datum, this.map, gMarker, PopupPOI);
      gMarker.popup = popup;

      gMarker.customSelect = () => popup.open();
      gMarker.customUnselect = () => popup.close();

      this._listeners.push(
        this.GM.event.addListener(gMarker, 'click', () => {
          if (gMarker.clickable) {
            if (popup.timeout) {
              clearTimeout(popup.timeout);
            }
            if (!popup.opened) {
              gMarker.customSelect();
            } else {
              gMarker.customUnselect();
            }
          }
        }),
      );

      return gMarker;
    },

    getCollaboratorsDatum(person) {
      const { address, details } = person;
      const site = this.study.sites.find(site => site.id.toString() === details.actualSiteID.toString());
      const siteData = person.sites.find(site => site.id.toString() === details.actualSiteID.toString());

      return {
        jllId: person.details.jllId,
        name: 'Collaborateur',
        address,
        actualSite: site.name,
        actualSiteAddress: site.address,
        actualTime: details.actualTime,
        actualSiteData: siteData,
      };
    },

    addCollaboratorMarker(person) {
      const position = { lat: person.latLng[0], lng: person.latLng[1] };
      this.bounds.extend(position);

      const gMarker = new this.GM.Marker({
        position,
        id: person.id,
        icon: this.circleIcon,
        zIndex: 0,
      });

      gMarker.setOpacity(1.0);
      const datum = this.getCollaboratorsDatum(person, 0);
      const popup = this.createPopup(datum, this.map, gMarker, PopupCollaborator);
      gMarker.popup = popup;

      gMarker.customSelect = () => popup.open();
      gMarker.customUnselect = () => popup.close();

      this._listeners.push(
        this.GM.event.addListener(gMarker, 'click', () => {
          if (popup.timeout) {
            clearTimeout(popup.timeout);
          }
          if (!popup.opened) {
            gMarker.customSelect();
          } else {
            gMarker.customUnselect();
          }
        }),
      );

      return gMarker;
    },

    getDefaultSiteImage(image) {
      return image || assetProvider.placeHolderSite;
    },

    getActualSiteDatum(site, idx) {
      return {
        name: site.name,
        address: site.address,
        averageTime: this.$store.getters.screen().actualSites[idx].details.averageTime,
        // averageTime: 59,
      };
    },

    addActualSiteMarker(site, idx) {
      const icon = this.generateIcon(assetProvider.poiHome);
      const position = { lat: site.lat_lng[0], lng: site.lat_lng[1] };
      this.bounds.extend(position);

      const gMarker = new this.GM.Marker({
        position,
        id: site.name,
        icon,
        zIndex: 2,
        title: site.name,
      });

      const datum = this.getActualSiteDatum(site, idx);
      const popup = this.createPopup(datum, this.map, gMarker, PopupActualSite);
      gMarker.popup = popup;

      gMarker.customSelect = () => popup.open();
      gMarker.customUnselect = () => popup.close();

      this._listeners.push(
        this.GM.event.addListener(gMarker, 'click', () => {
          if (popup.timeout) {
            clearTimeout(popup.timeout);
          }
          if (!popup.opened) {
            gMarker.customSelect();
          } else {
            gMarker.customUnselect();
          }
        }),
      );

      return gMarker;
    },

    getTargetSiteDatum(site, idx) {
      const data = this.$store.getters.screen();
      // const data = { sites: [] };

      return {
        image: this.getDefaultSiteImage(site.picture_path),
        name: site.name,
        address: site.address,
        difference: data.sites[idx] ? data.sites[idx].details.averageTimeDiff : null,
        averageTime: data.sites[idx] && data.sites[idx].details ? data.sites[idx].details.averageTime : null,
        //   difference: -3,
        //   averageTime: 59,
      };
    },

    addTargetSiteMarker(site, idx) {
      const icon = this.generateIcon(assetProvider[`poi${idx + 1}`]);
      const iconSelected = this.generateIcon(assetProvider[`poi${idx + 1}Active`]);
      const position = { lat: site.lat_lng[0], lng: site.lat_lng[1] };
      this.bounds.extend(position);

      const gMarker = new this.GM.Marker({
        map: this.map,
        position,
        id: site.id,
        icon,
        zIndex: 3,
        clickable: true,
      });

      gMarker.setOpacity(1.0);
      const datum = this.getTargetSiteDatum(site, idx);
      const popup = this.createPopup(datum, this.map, gMarker, PopupFutureSite);
      gMarker.popup = popup;

      gMarker.customSelect = () => {
        popup.open();
        gMarker.setIcon(iconSelected);
      };
      gMarker.customUnselect = () => {
        popup.close();
        gMarker.setIcon(icon);
      };

      this._listeners.push(
        this.GM.event.addListener(gMarker, 'click', () => {
          if (popup.timeout) {
            clearTimeout(popup.timeout);
          }
          if (idx !== this.activeIndex) {
            gMarker.customSelect();
            this.$store.dispatch('updateIndex', idx);
            if (!this.isDesktop || this.$route.name === 'Controller') {
              this.$socket.invoke('action', {
                action: 'upadteIndex',
                parameters: idx,
              });
            }
          } else {
            gMarker.customUnselect();
            this.$store.dispatch('updateIndex', -1);
            if (!this.isDesktop || this.$route.name === 'Controller') {
              this.$socket.invoke('action', {
                action: 'upadteIndex',
                parameters: -1,
              });
            }
          }
        }),
      );

      return gMarker;
    },

    updateExcludedCollaborators() {
      if (!this.study || !this.study.withWalkingTimes) return;

      if (this.study.maximum_travel_time_enabled === false) {
        this.collaboratorsMarkers.forEach(marker => marker.setIcon(this.circleIcon));
        return;
      }

      const currentSitesOnly = this.study.sites.filter(site => site.type_id === 2).length === 0;
      const currentTransportFilter = this.globalCurrentMatchingTime;
      const generalCurrentTransportFilter = currentTransportFilter
        .replaceAll(/\d/g, '') // replace 'public2025' or 'public2030' or 'public2027' or 'public2031' to 'public'
        .replaceAll('_without_toll', ''); // replace 'private_without_toll' to 'private'
      let maximumTravelTimeForCurrentTransport = this.study[generalCurrentTransportFilter + '_maximum_travel_time'];

      this.collaboratorsMarkers.forEach(marker => {
        if (currentSitesOnly) {
          marker.setIcon(this.circleIcon);
          return;
        }

        const collaborator = this.collaborators.find(collaborator => collaborator.id === marker.id);
        if (!collaborator) return;

        let collaboratorTransport;
        if (currentTransportFilter === 'custom') {
          collaboratorTransport = collaborator.details.transportType;
          maximumTravelTimeForCurrentTransport = this.study[collaboratorTransport + '_maximum_travel_time'];
        }

        const allTargetSitesTooFar = this.globalTargetSiteFilter.every(targetSiteId => {
          const collaboratorTargetSite = collaborator.sites.find(site => site.id === targetSiteId);
          if (!collaboratorTargetSite) return true;

          if (currentTransportFilter === 'optimized') {
            collaboratorTransport = collaboratorTargetSite.optimizedSite;
            maximumTravelTimeForCurrentTransport = this.study[collaboratorTransport + '_maximum_travel_time'];
          }

          const collaboratorTimeForCurrentTransport = collaboratorTargetSite.times[0][collaboratorTransport || currentTransportFilter] || 0;

          return maximumTravelTimeForCurrentTransport < collaboratorTimeForCurrentTransport;
        });

        marker.setIcon(allTargetSitesTooFar ? this.circleIconExcluded : this.circleIcon);
      });
    },
  },
};
</script>

<style lang="scss">
@use '@/styles/colors.scss';

@keyframes fadein {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeout {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.9);
  }
}

.map-container {
  flex: 1;
  box-sizing: border-box;
  margin-left: 55px;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    position: absolute !important;
    width: 100vw;
    height: 100vh;
  }

  #heatmap {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .popup-tip-anchor {
    animation: fadein 0.3s;
    height: 0;
    position: absolute;
    &.leaving {
      animation: fadeout 0.3s;
    }
  }

  .popup-bubble-anchor {
    position: absolute;
    width: 100%;
    bottom: 0px;
    left: 0;
  }

  .popup-bubble-content {
    position: absolute;
    left: 0;
    transform: translate(-50%, -100%);
    overflow: visible;
    background-color: white;
    box-sizing: border-box;
    border-radius: 3px;
    font-family: 'Source Sans Pro';
    padding: 15px 6px 15px 15px;
    box-shadow: 0px 0px 23px 1px rgba(0, 0, 0, 0.3);
  }

  .popup-bubble-content::after {
    content: '';
    position: absolute;
    top: 100%;
    margin-top: -1px;
    left: 50%;
    -webkit-transform: translate(-50%, 0);
    transform: translate(-50%, 0);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
  }
}
</style>
